import 'bootstrap/dist/css/bootstrap.min.css';
import '@fortawesome/fontawesome-free/css/all.min.css';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { Toaster } from 'react-hot-toast';
import Sidebar from './components/layout/Sidebar';
import routes from './routes';
import styled, { createGlobalStyle } from 'styled-components';
import React, { useState } from 'react';

const PrivateRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return <LoaderContainer><Spinner /></LoaderContainer>;
  }

  return user ? children : <Navigate to="/login" replace />;
};

const DashboardLayout = ({ children }) => {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  return (
    <DashboardContainer>
      <Sidebar 
        isCollapsed={sidebarCollapsed} 
        onToggleCollapse={setSidebarCollapsed} 
      />
      <MainContentWrapper $sidebarCollapsed={sidebarCollapsed}>
        <MainContent>
          {children}
        </MainContent>
      </MainContentWrapper>
    </DashboardContainer>
  );
};

const PublicLayout = ({ children }) => {
  return (
    <PublicContainer>
      <PublicContent>
        {children}
      </PublicContent>
    </PublicContainer>
  );
};

function App() {
  return (
    <Router>
      <GlobalStyles />
      <AuthProvider>
        <Toaster 
          position="top-right" 
          toastOptions={{
            duration: 4000,
            style: {
              background: '#363636',
              color: '#fff',
            },
          }} 
        />
        <Routes>
          {routes.map((route, index) => {
            if (route.requiresAuth) {
              return (
                <Route
                  key={index}
                  path={route.path}
                  element={
                    <PrivateRoute>
                      <DashboardLayout>
                        {route.element}
                      </DashboardLayout>
                    </PrivateRoute>
                  }
                  exact={route.exact}
                />
              );
            }

            return (
              <Route
                key={index}
                path={route.path}
                element={
                  <PublicLayout>
                    {route.element}
                  </PublicLayout>
                }
                exact={route.exact}
              />
            );
          })}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </AuthProvider>
    </Router>
  );
}

// Global Styles
const GlobalStyles = createGlobalStyle`
  :root {
    --primary-color: #182848;
    --primary-light: #27496d;
    --accent-color: #4cc9f0;
    --light-color: #f5f9ff;
    --dark-color: #0f1b2b;
    --success-color: #38b000;
    --danger-color: #f72585;
    --warning-color: #ffb703;
    --info-color: #00b4d8;
    --sidebar-width: 250px;
    --sidebar-collapsed-width: 80px;
    --transition: all 0.3s ease;
    --border-radius: 8px;
  }

  body {
    margin: 0;
    padding: 0;
    font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
    background-color: #f8f9fa;
    color: #333;
    line-height: 1.6;
  }

  a {
    text-decoration: none;
    color: inherit;
  }

  .content-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 1.5rem;
    padding-bottom: 1rem;
    border-bottom: 1px solid #dee2e6;

    h1 {
      font-size: 1.8rem;
      color: var(--dark-color);
      margin: 0;
    }
  }

  /* Scrollbar styling */
  ::-webkit-scrollbar {
    width: 8px;
    height: 8px;
  }

  ::-webkit-scrollbar-track {
    background: #f1f1f1;
  }

  ::-webkit-scrollbar-thumb {
    background: #c1c1c1;
    border-radius: 4px;
  }

  ::-webkit-scrollbar-thumb:hover {
    background: #a8a8a8;
  }
`;

// Styled Components
const DashboardContainer = styled.div`
  display: flex;
  min-height: 100vh;
  background-color: #f5f7fa;
  position: relative;
`;

const MainContentWrapper = styled.div`
  flex-grow: 1;
  padding-left: ${props => props.$sidebarCollapsed ? 'var(--sidebar-collapsed-width)' : 'var(--sidebar-width)'};
  transition: var(--transition);
  width: 100%;
  min-height: 100vh;
  display: flex;
  flex-direction: column;

  @media (max-width: 768px) {
    padding-left: 0 !important;
    
    .sidebar.mobile-open ~ & {
      transform: translateX(var(--sidebar-width));
      overflow: hidden;
      position: fixed;
      width: 100%;
    }
  }
`;

const MainContent = styled.main`
  flex: 1;
  padding: 2rem;
  background-color: var(--light-color);
  overflow-x: hidden;
  position: relative;

  @media (max-width: 1200px) {
    padding: 1.5rem;
  }

  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

const PublicContainer = styled.div`
  display: flex;
  min-height: 100vh;
  background-color: #f8f9fa;
  width: 100%;
`;

const PublicContent = styled.main`
  flex: 1;
  padding: 2rem;
  max-width: 1200px;
  margin: 0 auto;
  width: 100%;

  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

const LoaderContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100vh;
  width: 100vw;
  background-color: rgba(255, 255, 255, 0.8);
  position: fixed;
  top: 0;
  left: 0;
  z-index: 9999;
`;

const Spinner = styled.div`
  width: 50px;
  height: 50px;
  border: 5px solid #f3f3f3;
  border-top: 5px solid var(--primary-color);
  border-radius: 50%;
  animation: spin 1s linear infinite;

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

export default App;